import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import type { BinaryReader } from '../BinaryReader';

/**
 * Payment / cash codec. Mirrors PaymentModule (server) and CashPage.cs (Unity).
 *
 * Read-path (Cash/AgentRequest/AgentList/TransactionList) is fully wired. Money ACTIONS
 * (Pay/RequestPayback/CancelPayback) are encodable but must not be fired against the live DB
 * without explicit approval. Most actions acknowledge via a SendMessage(0) toast and a fresh
 * Cash(31) dashboard rather than a typed reply.
 */

/** C2S Cash(31): empty body — request the cash dashboard. */
export function encodeCashRequest(): Uint8Array {
  return beginMessage(MessageTag.Cash).toUint8Array();
}

export interface CashState {
  balance: bigint;
  isAgent: boolean;
  agentCode: string;
  /** True when a payback (withdrawal) request is pending. */
  hasPayback: boolean;
  /** Pending payback amount (abs). Always on the wire; only meaningful when hasPayback. */
  paybackAmount: bigint;
}

/** S2C Cash(31): long balance, bool isAgent, string agentCode, bool hasPayback, long paybackAmount (all 5 always present). */
export function decodeCash(r: BinaryReader): CashState {
  return {
    balance: r.getLong(),
    isAgent: r.getBool(),
    agentCode: r.getString(),
    hasPayback: r.getBool(),
    paybackAmount: r.getLong(),
  };
}

/** C2S Pay(36): deposit amount (long). Sentinel 9110 triggers the server's test-payment path. */
export function encodePay(amount: bigint): Uint8Array {
  return beginMessage(MessageTag.Pay).putLong(amount).toUint8Array();
}

/** S2C Pay(36): the payment-gateway URL (only sent on success). */
export function decodePayUrl(r: BinaryReader): string {
  return r.getString();
}

/** C2S RequestPayback(35): withdrawal amount (long). No typed reply (server re-sends Cash). */
export function encodeRequestPayback(amount: bigint): Uint8Array {
  return beginMessage(MessageTag.RequestPayback).putLong(amount).toUint8Array();
}

/** C2S CancelPayback(34): empty body. (The S2C CancelPayback reply is dead in this build.) */
export function encodeCancelPayback(): Uint8Array {
  return beginMessage(MessageTag.CancelPayback).toUint8Array();
}

/** C2S AgentRequest(33): empty body — become an agent / fetch a referral code. */
export function encodeAgentRequest(): Uint8Array {
  return beginMessage(MessageTag.AgentRequest).toUint8Array();
}

/** S2C AgentRequest(33): the newly generated referral code. */
export function decodeAgentCode(r: BinaryReader): string {
  return r.getString();
}

/** C2S AgentList(39): empty body — request the list of referred users. */
export function encodeAgentListRequest(): Uint8Array {
  return beginMessage(MessageTag.AgentList).toUint8Array();
}

export interface AgentList {
  totalUserCount: number;
  /** Pre-rendered Persian display rows (NOT a structured DTO). */
  users: string[];
}

/** S2C AgentList(39): int totalUserCount, then a ushort-prefixed string[] of display rows. */
export function decodeAgentList(r: BinaryReader): AgentList {
  return {
    totalUserCount: r.getInt(),
    users: r.getStringArray(),
  };
}

/** C2S TransactionList(38): empty body — request transaction + payback history. */
export function encodeTransactionListRequest(): Uint8Array {
  return beginMessage(MessageTag.TransactionList).toUint8Array();
}

/** S2C TransactionList(38): a ushort-prefixed string[] of pre-rendered Persian rows. */
export function decodeTransactionList(r: BinaryReader): string[] {
  return r.getStringArray();
}

/** C2S RecheckPayments(37): empty body. Server is a no-op now (replies with a toast). */
export function encodeRecheckPayments(): Uint8Array {
  return beginMessage(MessageTag.RecheckPayments).toUint8Array();
}
